/**
 * EditableField — click-to-edit text input with explicit Save/Cancel.
 *
 * Behaviors:
 *   - Idle: text renders as if non-editable, cursor:text on hover
 *   - Hover: brand-tint background + brand-110 border
 *   - Focus: white bg + brand-80 border + soft focus ring + Save/Cancel bar
 *   - Empty: italic placeholder via data-placeholder + ::before
 *   - Single-line: Enter commits; Escape reverts
 *   - Multiline: Ctrl+Enter commits; Escape reverts
 *   - Paste: sanitized to plain text (Firefox/Safari workaround)
 *   - Blur: reverts unless Save button was clicked
 *   - Save/Cancel buttons appear below field when editing
 */

import { useEffect, useRef, useState } from 'react'
import { makeStyles, mergeClasses } from '@fluentui/react-components'
import { Checkmark16Regular, Dismiss16Regular } from '@fluentui/react-icons'
import { bayerColors } from '../theme'

const useStyles = makeStyles({
  wrapper: { position: 'relative', display: 'inline-block', width: '100%' },
  field: {
    display: 'inline-block',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
    borderRadius: '4px',
    padding: '1px 4px',
    marginTop: '-1px',
    marginRight: '-4px',
    marginBottom: '-1px',
    marginLeft: '-4px',
    outline: 'none',
    cursor: 'text',
    minWidth: '32px',
    wordBreak: 'break-word',
    lineHeight: 'inherit',
    transitionProperty: 'background, border-color',
    transitionDuration: '100ms',
    ':hover': {
      background: bayerColors.navyLight,
      borderTopColor: '#54749a',
      borderRightColor: '#54749a',
      borderBottomColor: '#54749a',
      borderLeftColor: '#54749a',
    },
    ':focus': {
      background: '#FFFFFF',
      borderTopColor: bayerColors.navyMid,
      borderRightColor: bayerColors.navyMid,
      borderBottomColor: bayerColors.navyMid,
      borderLeftColor: bayerColors.navyMid,
      boxShadow: '0 0 0 3px rgba(43,107,166,0.12)',
    },
  },
  multi: {
    display: 'block',
    padding: '4px 8px',
    marginTop: '-4px',
    marginRight: '-8px',
    marginBottom: '-4px',
    marginLeft: '-8px',
    whiteSpace: 'pre-wrap',
  },
  empty: {
    '::before': {
      content: 'attr(data-placeholder)',
      color: '#A0A0A0',
      fontStyle: 'italic',
      pointerEvents: 'none',
    },
    ':focus::before': { display: 'none' },
  },
  disabled: {
    cursor: 'default',
    ':hover': {
      background: 'transparent',
      borderTopColor: 'transparent',
      borderRightColor: 'transparent',
      borderBottomColor: 'transparent',
      borderLeftColor: 'transparent',
    },
  },
  actions: {
    display: 'flex',
    gap: '4px',
    marginTop: '4px',
    marginLeft: '-4px',
  },
  actionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 500,
    border: '1px solid',
    borderRadius: '4px',
    cursor: 'pointer',
    lineHeight: '18px',
  },
  // Griffel rejects the `borderColor` shorthand at the top level too —
  // expand to directional properties.
  saveBtn: {
    background: bayerColors.navyMid,
    borderTopColor: bayerColors.navyMid,
    borderRightColor: bayerColors.navyMid,
    borderBottomColor: bayerColors.navyMid,
    borderLeftColor: bayerColors.navyMid,
    color: '#fff',
    ':hover': { background: '#1a4a75' },
  },
  cancelBtn: {
    background: '#fff',
    borderTopColor: '#d0d0d0',
    borderRightColor: '#d0d0d0',
    borderBottomColor: '#d0d0d0',
    borderLeftColor: '#d0d0d0',
    color: '#444',
    ':hover': { background: '#f5f5f5' },
  },
})

export interface EditableFieldProps {
  value: string | null | undefined
  path: string
  onSave: (path: string, value: string) => void
  multiline?: boolean
  placeholder?: string
  disabled?: boolean
  className?: string
  ariaLabel?: string
}

export function EditableField({
  value,
  path,
  onSave,
  multiline = false,
  placeholder = 'Click to add…',
  disabled = false,
  className,
  ariaLabel,
}: EditableFieldProps) {
  const styles = useStyles()
  const ref = useRef<HTMLSpanElement | HTMLDivElement>(null)
  const lastCommittedRef = useRef(value ?? '')
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (document.activeElement === el) return
    const incoming = value ?? ''
    if (el.innerText !== incoming) el.innerText = incoming
    lastCommittedRef.current = incoming
  }, [value])

  function doSave() {
    const el = ref.current
    if (!el) return
    const next = el.innerText.trim()
    if (next !== lastCommittedRef.current) {
      lastCommittedRef.current = next
      onSave(path, next)
    }
    setEditing(false)
    el.blur()
  }

  function doCancel() {
    const el = ref.current
    if (!el) return
    el.innerText = lastCommittedRef.current
    setEditing(false)
    el.blur()
  }

  if (disabled) {
    const Tag = multiline ? 'div' : 'span'
    return (
      <Tag
        className={mergeClasses(styles.field, multiline && styles.multi, styles.disabled, className)}
        aria-label={ariaLabel}
      >
        {value || ''}
      </Tag>
    )
  }

  const isEmpty = !value || value.length === 0
  const commonProps = {
    ref: ref as React.RefObject<HTMLSpanElement & HTMLDivElement>,
    contentEditable: 'plaintext-only' as const,
    suppressContentEditableWarning: true,
    role: 'textbox' as const,
    'aria-label': ariaLabel,
    'aria-multiline': multiline,
    'data-field-path': path,
    'data-placeholder': placeholder,
    spellCheck: true,
    onFocus: () => setEditing(true),
    // Leaving the field commits it (#1247). It used to revert, on the reasoning
    // in the deleted comment here — "no accidental save" — which weighed the
    // wrong risk: an accidental save writes a DemandFieldAuditLog row, notifies
    // nobody, and is undone by editing again, while an accidental revert is
    // silent and unrecoverable. Escape is the explicit discard.
    //
    // `doSave` is idempotent against `lastCommittedRef`, so the Save and Cancel
    // buttons' own `el.blur()` lands here harmlessly and the old savingRef /
    // 150ms dance is gone with it.
    onBlur: () => doSave(),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (!multiline && e.key === 'Enter') {
        e.preventDefault()
        doSave()
      } else if (multiline && e.key === 'Enter' && e.ctrlKey) {
        e.preventDefault()
        doSave()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        doCancel()
      }
    },
    onPaste: (e: React.ClipboardEvent) => {
      e.preventDefault()
      const text = e.clipboardData.getData('text/plain')
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) return
      const range = selection.getRangeAt(0)
      range.deleteContents()
      range.insertNode(document.createTextNode(text))
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)
    },
    className: mergeClasses(
      styles.field,
      multiline && styles.multi,
      isEmpty && !editing && styles.empty,
      className,
    ),
  }

  return (
    <div className={styles.wrapper}>
      {multiline ? <div {...commonProps}>{value || ''}</div> : <span {...commonProps}>{value || ''}</span>}
      {editing && (
        <div className={styles.actions}>
          {/* Both handlers on purpose. `onMouseDown` + preventDefault keeps a
              mouse click from blurring the field first, which is what makes the
              Cancel button able to cancel at all. `onClick` is what a real
              button answers to — without it, Enter or Space on a focused Save
              did nothing, and since blur used to revert, tabbing towards it
              destroyed the text and unmounted the button mid-Tab. Firing both
              on a mouse click is harmless: doSave and doCancel are idempotent. */}
          <button
            type="button"
            className={mergeClasses(styles.actionBtn, styles.saveBtn)}
            onMouseDown={(e) => {
              e.preventDefault()
              doSave()
            }}
            onClick={doSave}
            aria-label="Save"
          >
            <Checkmark16Regular /> Save
          </button>
          <button
            type="button"
            className={mergeClasses(styles.actionBtn, styles.cancelBtn)}
            onMouseDown={(e) => {
              e.preventDefault()
              doCancel()
            }}
            onClick={doCancel}
            aria-label="Cancel"
            title="Discard changes (Esc)"
          >
            <Dismiss16Regular /> Cancel
          </button>
        </div>
      )}
    </div>
  )
}
